import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuthStore } from '@/lib/auth/authStore';
import { useBrandContextStore } from './brandContextStore';

interface ProfilePrefs {
  id: number;
  preferences?: { lastBrandId?: number | null } | null;
}

/**
 * La última marca abierta sigue al usuario a cualquier dispositivo (095).
 *
 * Dos direcciones, y las dos con freno:
 *
 *  1. **Servidor → store, una vez por sesión de usuario.** Al tener el perfil,
 *     si `preferences.lastBrandId` existe y está entre las marcas visibles, se
 *     selecciona con origen `server`. Sólo la primera vez (`hydratedFor`): si
 *     se hidratara en cada refetch, cambiar de marca y recibir el perfil viejo
 *     desharía el cambio.
 *  2. **Store → servidor, sólo lo que eligió la persona.** Un cambio con
 *     origen `user` (o `repair`, para que la corrección no se repita en cada
 *     aparato) se envía con `PATCH /profile/preferences`, con un pequeño
 *     debounce para quien recorre el selector con las flechas.
 *
 * `localStorage` sigue siendo la caché de arranque: la pantalla no espera al
 * perfil para pintar la marca de la última vez en ESTE navegador.
 */
export function useBrandPreferenceSync(visibleBrandIds: number[]) {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const token = useAuthStore((s) => s.token);
  const app = useAuthStore((s) => s.app);
  const profileQ = useQuery({
    queryKey: queryKeys.profile.me,
    queryFn: () => api.get<ProfilePrefs>('/profile'),
    enabled: !!token && app !== 'operator',
    staleTime: 60_000,
  });

  // 1 · hidratar
  useEffect(() => {
    if (!userId || !profileQ.data || visibleBrandIds.length === 0) return;
    const st = useBrandContextStore.getState();
    if (st.hydratedFor === userId) return;
    const remoto = profileQ.data.preferences?.lastBrandId ?? null;
    if (remoto && visibleBrandIds.includes(remoto) && remoto !== st.selectedBrandId) {
      st.setSelectedBrandId(remoto, 'server');
    }
    st.markHydrated(userId);
  }, [userId, profileQ.data, visibleBrandIds]);

  // 2 · escribir
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!userId || app === 'operator') return;
    const unsub = useBrandContextStore.subscribe((state, prev) => {
      if (state.selectedBrandId === prev.selectedBrandId) return;
      if (state.origin !== 'user' && state.origin !== 'repair') return;
      if (state.hydratedFor !== userId) return; // antes de hidratar no se pisa lo del servidor
      if (timer.current) window.clearTimeout(timer.current);
      const id = state.selectedBrandId;
      timer.current = window.setTimeout(() => {
        api
          .patch('/profile/preferences', { lastBrandId: id })
          .then(() => qc.invalidateQueries({ queryKey: queryKeys.profile.me }))
          .catch(() => {
            // Silencio a propósito: es una comodidad, no un dato del usuario.
            // Fallar aquí no puede interrumpir el cambio de marca.
          });
      }, 800);
    });
    return () => {
      unsub();
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [userId, app, qc]);
}
